import React, { useEffect, useRef, useState } from "react";
import { Cloud, requestCloudReveal } from "./CloudTransition";

const F = { fontFamily: "'Fredoka', sans-serif" };

// How long the launch plays before navigation. Matches the CSS below.
const LAUNCH_MS = 780;

// Matches the iOS games hub: the world video drifts in gentle slow motion.
const MAP_VIDEO_RATE = 0.6;

// Each marker is pinned to the matching landmark in adventure-map-wide.jpg using
// percentage coordinates (x/y are the centre of the medallion). The artwork is
// 3168 × 1344 (landscape), so the map scales to fill the viewport height and the
// player scrolls horizontally (to the right) to reach more games. `label` decides
// whether the name pill sits above or below the pin so markers near the
// top/bottom edges stay on the map.
const ICONS = "/assets/games/map-icons";

export const MAP_MARKERS = [
  { id: "color-bubble-pop", title: "Color Bubble Pop", img: `${ICONS}/color-bubble-pop.png`, ring: "#8351C1", x: 10, y: 12, label: "bottom" },
  { id: "story-puzzle", title: "Story Puzzle", img: `${ICONS}/story-puzzle.png`, ring: "#2EAD72", x: 26, y: 55, label: "bottom" },
  { id: "bubble-pop", title: "Bubble Pop Math", img: `${ICONS}/bubble-pop-math.png`, ring: "#2A6FD8", x: 9, y: 80, label: "top" },
  { id: "city-helpers", title: "Pixley City", img: `${ICONS}/pixley-city.png`, ring: "#EB525C", x: 40, y: 38, label: "bottom" },
  { id: "animal-sound-farm", title: "Animal Sound Farm", short: "Animal Farm", img: `${ICONS}/animal-sound-farm.png`, ring: "#36A54B", x: 60, y: 34, label: "bottom" },
  { id: "critter-crossing", title: "Critter Crossing", img: `${ICONS}/critter-crossing.png`, ring: "#2E9E77", x: 39, y: 82, label: "top" },
  { id: "feed-monster", title: "Feed Munchy", img: `${ICONS}/feed-munchy.png`, ring: "#F0701A", x: 68, y: 68, label: "top" },
  { id: "math-castle-defense", title: "Castle Defense", img: `${ICONS}/castle-defense.png`, ring: "#5C3FBE", x: 77, y: 32, label: "bottom" },
  { id: "word-web-defender", title: "Shape Web Defender", short: "Web Defender", img: `${ICONS}/shape-web-defender.png`, ring: "#6040B9", x: 93, y: 55, label: "left" },
];

const AdventureMap = ({ onSelect, canLaunch }) => {
  // Launch choreography: zoom the map into the tapped medallion while clouds
  // sweep in from the sides; navigate once the screen is covered.
  const [launch, setLaunch] = useState(null); // marker being launched, or null
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Reduced-motion users get the calm static map instead of a moving world.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVideoFailed(true);
      return;
    }
    video.playbackRate = MAP_VIDEO_RATE;
    // Muted autoplay is allowed everywhere, but nudge it in case the browser
    // paused it; a rejection just leaves the image underneath showing.
    video.play?.().catch(() => {});
  }, [videoFailed]);

  const handlePick = (marker) => {
    if (launch) return; // ignore double-taps mid-animation
    // Ask permission BEFORE the launch choreography — a gate (e.g. the
    // members-only wall) must leave the map intact, not zoomed into clouds.
    if (canLaunch && !canLaunch(marker.id)) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) {
      onSelect(marker.id);
      return;
    }
    setLaunch(marker);
    timerRef.current = window.setTimeout(() => {
      // Arm the app-level reveal so the destination page opens through clouds.
      requestCloudReveal();
      onSelect(marker.id);
    }, LAUNCH_MS);
  };

  return (
  <div className={`pm-stage${launch ? " is-launching" : ""}`}>
    <div className="pm-scroll">
      <div
        className="pm-map"
        style={launch ? { transformOrigin: `${launch.x}% ${launch.y}%` } : undefined}
      >
        <img
          src="/assets/games/adventure-map-wide.jpg"
          alt="Pixley adventure map full of games to explore"
          className="pm-map__img"
          draggable={false}
        />
        {/* The living world from the iOS games hub — a muted, seamless loop.
            The static map stays underneath as an instant fallback. */}
        {!videoFailed && (
          <video
            ref={videoRef}
            className="pm-map__video"
            src="/assets/games/games_map.mp4"
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            aria-hidden="true"
            tabIndex={-1}
            onError={() => setVideoFailed(true)}
          />
        )}
        <div className="pm-map__glow" aria-hidden />

        {MAP_MARKERS.map((marker, index) => (
          <button
            key={marker.id}
            type="button"
            onClick={() => handlePick(marker)}
            aria-label={`Play ${marker.title}`}
            className={`pm-pin${launch?.id === marker.id ? " is-picked" : ""}`}
            style={{
              left: `${marker.x}%`,
              // Never let a medallion slide under the floating navbar: the
              // effective top is clamped to the safe area + pin half-height.
              top: `max(${marker.y}%, calc(var(--pm-safe-top) + clamp(26px, 2.5vw, 46px)))`,
            }}
          >
            <span
              className="pm-pin__bob"
              style={{ "--ring": marker.ring, animationDelay: `${(index % 5) * 0.24}s` }}
            >
              <img className="pm-pin__img" src={marker.img} alt="" draggable={false} />
            </span>
            <span className={`pm-pin__label pm-pin__label--${marker.label}`} style={{ ...F, color: marker.ring }}>
              {marker.short || marker.title}
            </span>
          </button>
        ))}
      </div>
    </div>

    <div className={`pm-clouds${launch ? " is-active" : ""}`} aria-hidden>
      <Cloud className="pm-cloud pm-cloud--l1" />
      <Cloud className="pm-cloud pm-cloud--l2" />
      <Cloud className="pm-cloud pm-cloud--r1" />
      <Cloud className="pm-cloud pm-cloud--r2" />
      <Cloud className="pm-cloud pm-cloud--b1" />
      <span className="pm-clouds__wash" />
    </div>

    <div className="pm-title">
      <span className="pm-title-main" style={F}>Pick your adventure</span>
      <span className="pm-title-sub" style={F}>Tap a spot to start playing!</span>
    </div>

    <div className="pm-hint" aria-hidden>
      <span style={F}>Scroll to explore</span>
      <svg className="pm-hint__arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M2 8h10M8 3.5 12.5 8 8 12.5" stroke="#0A4BAB" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>

    <style>{`
      .pm-stage {
        position: relative;
        width: 100%;
        height: 100dvh;
        overflow: hidden;
        -webkit-user-select: none;
        user-select: none;
        /* Height of the floating navbar zone; pins are clamped below it. */
        --pm-safe-top: clamp(96px, 13vh, 132px);
      }
      .pm-scroll {
        position: absolute;
        inset: 0;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
      }
      .pm-scroll::-webkit-scrollbar { display: none; }

      .pm-map {
        position: relative;
        height: 100%;
        aspect-ratio: 3168 / 1344;
        width: auto;
        overflow: hidden;
      }
      .pm-map__img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .pm-map__video {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        /* Stretch-fill like iOS (videoGravity .resize) so the marker
           percentages keep lining up with the painted world. */
        object-fit: fill;
        pointer-events: none;
      }
      .pm-map__glow {
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,0.22), transparent 42%);
      }

      .pm-title {
        /* Bottom-left, mirroring the scroll hint — the floating navbar owns
           the top of the viewport, which previously swallowed this pill. */
        position: absolute;
        bottom: clamp(14px, 2.4vh, 28px);
        left: clamp(12px, 2vw, 26px);
        z-index: 8;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        line-height: 1.1;
        white-space: nowrap;
        border-radius: 9999px;
        background: rgba(255,255,255,0.82);
        backdrop-filter: blur(5px);
        -webkit-backdrop-filter: blur(5px);
        border: 2px solid rgba(255,255,255,0.92);
        box-shadow: 0 6px 18px rgba(11,26,51,0.18);
        padding: clamp(6px, 1vh, 12px) clamp(18px, 2.4vw, 34px);
        pointer-events: none;
      }
      .pm-title-main { font-size: clamp(18px, 2.9vw, 34px); font-weight: 700; color: #0A4BAB; }
      .pm-title-sub { font-size: clamp(11px, 1.5vw, 17px); font-weight: 600; color: #2A5A9F; margin-top: 2px; }

      .pm-hint {
        position: absolute;
        right: clamp(12px, 2vw, 26px);
        bottom: clamp(14px, 2.4vh, 28px);
        z-index: 8;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: clamp(6px, 1vh, 10px) clamp(12px, 1.6vw, 18px);
        border-radius: 9999px;
        background: rgba(255,255,255,0.9);
        border: 2px solid rgba(255,255,255,0.95);
        box-shadow: 0 6px 16px rgba(11,26,51,0.18);
        font-size: clamp(12px, 1.4vw, 16px);
        font-weight: 700;
        color: #0A4BAB;
        pointer-events: none;
      }
      .pm-hint__arrow {
        display: inline-block;
        font-size: 1.15em;
        animation: pmHintNudge 1.4s ease-in-out infinite;
      }
      @keyframes pmHintNudge {
        0%, 100% { transform: translateX(0); }
        50% { transform: translateX(5px); }
      }

      .pm-pin {
        position: absolute;
        transform: translate(-50%, -50%);
        display: grid;
        place-items: center;
        width: clamp(52px, 5vw, 92px);
        height: auto;
        aspect-ratio: 1;
        padding: 0;
        border: 0;
        background: transparent;
        cursor: pointer;
        z-index: 3;
        -webkit-tap-highlight-color: transparent;
      }
      .pm-pin:focus-visible { outline: none; }
      .pm-pin:focus-visible .pm-pin__bob { box-shadow: 0 0 0 4px rgba(255,255,255,0.95), 0 0 0 7px var(--ring); }

      .pm-pin__bob {
        display: grid;
        place-items: center;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: radial-gradient(circle at 32% 26%, #FFFFFF 0%, #FFFFFF 55%, #F3F7FF 100%);
        border: 3.5px solid var(--ring);
        box-shadow: 0 6px 14px rgba(11,26,51,0.28), inset 0 -4px 8px rgba(11,26,51,0.06);
        animation: pmPinBob 2.6s ease-in-out infinite;
        transition: transform 0.16s ease, box-shadow 0.16s ease;
      }
      .pm-pin__img {
        width: 88%;
        height: 88%;
        border-radius: 50%;
        object-fit: cover;
        pointer-events: none;
      }

      .pm-pin__label {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        padding: clamp(2px, 0.4vw, 5px) clamp(9px, 1vw, 15px);
        border-radius: 9999px;
        background: rgba(255,255,255,0.95);
        font-size: clamp(11px, 1.35vw, 19px);
        font-weight: 700;
        white-space: nowrap;
        pointer-events: none;
        box-shadow: 0 3px 8px rgba(11,26,51,0.16);
        border: 1.5px solid rgba(255,255,255,0.9);
        transition: transform 0.16s ease;
      }
      .pm-pin__label--bottom { top: calc(100% + 6px); }
      .pm-pin__label--top { bottom: calc(100% + 6px); }
      .pm-pin__label--left { right: calc(100% + 7px); left: auto; transform: none; top: 50%; margin-top: -0.7em; }
      .pm-pin__label--right { left: calc(100% + 7px); transform: none; top: 50%; margin-top: -0.7em; }

      .pm-pin:hover { z-index: 6; }
      .pm-pin:hover .pm-pin__bob {
        animation-play-state: paused;
        transform: scale(1.16);
        box-shadow: 0 10px 20px rgba(11,26,51,0.32), inset 0 -4px 8px rgba(11,26,51,0.06);
      }
      .pm-pin:active .pm-pin__bob { transform: scale(1.04); }
      .pm-pin:hover .pm-pin__label--bottom { transform: translateX(-50%) scale(1.06); }
      .pm-pin:hover .pm-pin__label--top { transform: translateX(-50%) scale(1.06); }

      @keyframes pmPinBob {
        0%, 100% { translate: 0 0; }
        50% { translate: 0 -5px; }
      }
      /* ---- Launch: zoom into the tapped medallion, clouds close over ---- */
      .pm-map {
        transition: transform ${LAUNCH_MS}ms cubic-bezier(0.6, 0.02, 0.3, 1);
      }
      .is-launching .pm-map {
        transform: scale(2.7);
      }
      .is-launching .pm-pin { pointer-events: none; }
      .pm-pin.is-picked { z-index: 7; }
      .pm-pin.is-picked .pm-pin__bob {
        animation: none;
        transform: scale(1.25);
        box-shadow: 0 0 0 6px rgba(255,255,255,0.65), 0 14px 26px rgba(11,26,51,0.35);
      }
      .is-launching .pm-title,
      .is-launching .pm-hint { opacity: 0; transition: opacity 0.25s ease; }

      .pm-clouds {
        position: absolute;
        inset: 0;
        z-index: 10;
        pointer-events: none;
        opacity: 0;
        overflow: hidden;
      }
      .pm-clouds.is-active { opacity: 1; }
      .pm-cloud {
        position: absolute;
        width: 110vmax;
        height: auto;
        filter: drop-shadow(0 18px 30px rgba(120,150,190,0.25)) blur(0.5px);
      }
      .pm-cloud--l1 { left: -140vmax; top: -16vmax; transition: transform ${LAUNCH_MS - 120}ms cubic-bezier(0.5, 0, 0.3, 1) 60ms; }
      .pm-cloud--l2 { left: -150vmax; bottom: -20vmax; width: 120vmax; transition: transform ${LAUNCH_MS - 80}ms cubic-bezier(0.5, 0, 0.3, 1) 20ms; }
      .pm-cloud--r1 { right: -140vmax; top: -12vmax; transition: transform ${LAUNCH_MS - 120}ms cubic-bezier(0.5, 0, 0.3, 1) 90ms; }
      .pm-cloud--r2 { right: -150vmax; bottom: -22vmax; width: 120vmax; transition: transform ${LAUNCH_MS - 80}ms cubic-bezier(0.5, 0, 0.3, 1); }
      .pm-cloud--b1 { left: 50%; bottom: -125vmax; width: 130vmax; transform: translateX(-50%); transition: transform ${LAUNCH_MS - 60}ms cubic-bezier(0.5, 0, 0.3, 1) 40ms; }
      .pm-clouds.is-active .pm-cloud--l1 { transform: translateX(112vmax); }
      .pm-clouds.is-active .pm-cloud--l2 { transform: translateX(116vmax); }
      .pm-clouds.is-active .pm-cloud--r1 { transform: translateX(-112vmax); }
      .pm-clouds.is-active .pm-cloud--r2 { transform: translateX(-116vmax); }
      .pm-clouds.is-active .pm-cloud--b1 { transform: translate(-50%, -95vmax); }

      /* Guarantees full coverage in the final beat, whatever the viewport
         shape, and hands off seamlessly to the reveal overlay's wash. */
      .pm-clouds__wash {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, #FFFFFF 0%, #F2F8FF 100%);
        opacity: 0;
      }
      .pm-clouds.is-active .pm-clouds__wash {
        opacity: 1;
        transition: opacity 200ms ease-in ${LAUNCH_MS - 240}ms;
      }

      @media (prefers-reduced-motion: reduce) {
        .pm-pin__bob { animation: none; }
        .pm-hint__arrow { animation: none; }
        .pm-map { transition: none; }
        .pm-cloud { transition: none; }
      }
    `}</style>
  </div>
  );
};

export default AdventureMap;
